/**
 * The three layers of `contracts/design-system.md` § Token architecture and the way they are
 * emitted (`research.md` W-12). A token is a name without its leading `--` and a value; a
 * semantic or component value may be a reference to another token, written `ref("neutral-1")`,
 * which emits as `var(--neutral-1)` and resolves to a raw value for the contrast test, or a mix
 * of two colour tokens, written `mix("color-accent-line", 10, "color-accent-bg")`, which emits as
 * `color-mix(in oklab, …)` over the two `var()`s and resolves by the same OKLab arithmetic.
 */
import { mixOklab } from "../colour/oklch.ts";

/** A reference from one layer to a token of a lower layer. */
export interface TokenRef {
  readonly ref: string;
}

/**
 * A colour derived from two colour tokens, `percent` of the first and the rest of the second,
 * interpolated in OKLab (2026-10-03, the tinted button's states). The browser computes it where the
 * stylesheet declares it, the document element, from whatever values the two tokens hold there, so
 * a derived colour follows a Team palette and a theme switch with no value of its own.
 */
export interface TokenMix {
  readonly mix: readonly [string, string];
  readonly percent: number;
}

export type TokenValue = string | TokenRef | TokenMix;

/** One flat set of tokens, keyed by name without the `--` prefix. */
export type TokenSet = Readonly<Record<string, TokenValue>>;

export function ref(name: string): TokenRef {
  return { ref: name };
}

/** `percent` (a whole number from 1 to 99) of the colour token `first`, mixed in OKLab with the colour token `second`. */
export function mix(first: string, percent: number, second: string): TokenMix {
  if (!Number.isInteger(percent) || percent <= 0 || percent >= 100) throw new Error(`a mix takes a whole percentage between 0 and 100, not ${percent}`);
  return { mix: [first, second], percent };
}

export function isRef(value: TokenValue): value is TokenRef {
  return typeof value === "object" && "ref" in value;
}

export function isMix(value: TokenValue): value is TokenMix {
  return typeof value === "object" && "mix" in value;
}

/** The complete token tree: primitives, the semantic layer per theme, the theme-invariant
 * chrome, the shadcn/ui aliases Spectrum UI's components read, the component layer, and the
 * density set. */
export interface TokenTree {
  readonly primitives: TokenSet;
  readonly semantic: { readonly light: TokenSet; readonly dark: TokenSet };
  readonly chrome: TokenSet;
  /** `--background`, `--primary`, `--radius` and the rest, each a reference to a Baton token (§ Spectrum UI and shadcn/ui variables). */
  readonly shadcn: TokenSet;
  readonly components: TokenSet;
  readonly density: { readonly comfortable: TokenSet; readonly compact: TokenSet };
  /** The names of every duration token; the reduced-motion override sets each to `0ms`. */
  readonly durations: readonly string[];
  /** The component tokens that time a looping animation (the skeleton's shimmer and pulse); the reduced-motion override stops each at `0ms` too, so nothing loops (§ Motion). */
  readonly loops: readonly string[];
  /** The component tokens that time a one-shot animation longer than the duration tiers (the B mark's sheen, 2026-09-29); the reduced-motion override sets each to `0ms` too, so it does not play (§ Motion). */
  readonly moments: readonly string[];
}

/** The custom-property form of a value: a reference becomes `var(--name)` and a mix `color-mix(in oklab, …)` over two of them. */
export function cssValue(value: TokenValue): string {
  if (isRef(value)) return `var(--${value.ref})`;
  if (isMix(value)) return `color-mix(in oklab, var(--${value.mix[0]}) ${value.percent}%, var(--${value.mix[1]}))`;
  return value;
}

function resolveThrough(value: TokenValue, sets: readonly TokenSet[], seen: ReadonlySet<string>): string {
  if (isMix(value)) {
    const [first, second] = value.mix;
    return mixOklab(resolveThrough(ref(first), sets, seen), resolveThrough(ref(second), sets, seen), value.percent / 100);
  }
  if (!isRef(value)) return value;
  const name = value.ref;
  if (seen.has(name)) throw new Error(`token reference cycle through --${name}`);
  const found = sets.map((set) => set[name]).find((v) => v !== undefined);
  if (found === undefined) throw new Error(`token --${name} is referenced but not defined`);
  return resolveThrough(found, sets, new Set([...seen, name]));
}

/** A value with every reference followed down to a raw string, and every mix computed from the raw values of its two tokens. The first set naming a token wins, so a caller can lay a palette's values over the static ones. */
export function resolve(value: TokenValue, ...sets: readonly TokenSet[]): string {
  return resolveThrough(value, sets, new Set());
}

function block(selector: string, set: TokenSet, indent = ""): string {
  const lines = Object.entries(set).map(([name, value]) => `${indent}  --${name}: ${cssValue(value)};`);
  return `${indent}${selector} {\n${lines.join("\n")}\n${indent}}`;
}

/**
 * The stylesheet: primitives, chrome and component tokens on `:root`; the light semantic
 * layer as the default; the shadcn/ui aliases on `:root`, which follow the theme because they
 * only reference themed tokens; the dark layer under `data-theme="dark"` and under the system
 * preference when no `data-theme` is set; density under `data-density`; and the single
 * reduced-motion override that resolves every duration, every animation loop and every one-shot moment to 0 ms (§ Motion).
 */
export function emitCss(tree: TokenTree): string {
  const zeroDurations: Record<string, string> = Object.fromEntries([...tree.durations, ...tree.loops, ...tree.moments].map((name) => [name, "0ms"]));
  const parts = [
    "/* Generated by packages/tokens/src/emit/build.ts from contracts/design-system.md. Do not edit. */",
    block(":root", { ...tree.primitives, ...tree.chrome, ...tree.components, ...tree.semantic.light }),
    block(":root", tree.shadcn),
    block(':root[data-theme="dark"]', tree.semantic.dark),
    `@media (prefers-color-scheme: dark) {\n${block(':root:not([data-theme="light"])', tree.semantic.dark, "  ")}\n}`,
    block(":root", tree.density.comfortable),
    block(':root[data-density="compact"]', tree.density.compact),
    block(':root[data-motion="reduced"]', zeroDurations),
    `@media (prefers-reduced-motion: reduce) {\n${block(':root:not([data-motion="full"])', zeroDurations, "  ")}\n}`,
  ];
  return `${parts.join("\n\n")}\n`;
}

/** The JSON form feature 016 consumes: every layer with references resolved to raw values. */
export function emitJson(tree: TokenTree): string {
  const resolveSet = (set: TokenSet, ...lower: readonly TokenSet[]) =>
    Object.fromEntries(Object.entries(set).map(([name, value]) => [name, resolve(value, set, ...lower)]));
  const out = {
    primitives: resolveSet(tree.primitives),
    chrome: resolveSet(tree.chrome, tree.primitives),
    components: resolveSet(tree.components, tree.primitives),
    semantic: {
      light: resolveSet(tree.semantic.light, tree.chrome, tree.primitives),
      dark: resolveSet(tree.semantic.dark, tree.chrome, tree.primitives),
    },
    shadcn: {
      light: resolveSet(tree.shadcn, tree.semantic.light, tree.chrome, tree.primitives),
      dark: resolveSet(tree.shadcn, tree.semantic.dark, tree.chrome, tree.primitives),
    },
    density: {
      comfortable: resolveSet(tree.density.comfortable, tree.primitives),
      compact: resolveSet(tree.density.compact, tree.primitives),
    },
  };
  return `${JSON.stringify(out, null, 2)}\n`;
}

/**
 * The Tailwind 4 theme (`research.md` W-12, and `contracts/design-system.md` § Spectrum UI and
 * shadcn/ui variables from 2026-09-15). The default theme is cleared and every namespace a
 * Spectrum UI or shadcn/ui class reads is re-populated from a token, so a utility resolves to
 * a token and no utility carries a raw value:
 *
 * - colours under their Baton names (`bg-bg-surface`) and under the shadcn names
 *   (`bg-primary`), the latter through the `--primary` aliases of the token stylesheet;
 * - the spacing multiplier, so `h-9` is nine steps of the 4 px `--space-2`;
 * - the radius ladder Spectrum UI derives from its 0.5 rem `--radius`: `rounded-sm`, `-md`,
 *   `-lg` and `-xl` are Baton's 4, 6, 8 and 12 px radii, `rounded-xs` half the smallest;
 * - the type steps under Baton's names and under Tailwind's (`text-sm` is the 14/20 body
 *   step; Tailwind's 18 px `text-lg` has no Baton step and takes the 20 px one);
 * - weights, the wide tracking of a keyboard hint (the micro step's), shadows, easings, the
 *   default transition timing and the spin and pulse loops, which run on the motion tokens so
 *   `data-motion="reduced"` stops them;
 * - a `dark` variant matching Baton's explicit `data-theme="dark"` and the system preference
 *   when no `data-theme` is set, the same two states the token stylesheet themes.
 *
 * The variable block is `inline reference`: utilities take the value directly and nothing is
 * emitted into `@layer theme`, where a Tailwind key such as `--radius-md` would otherwise
 * shadow the Baton token of the same name. So a name this block declares and `emitCss` does not
 * (`--font-weight-semibold`, `--shadow-drag`, `--tracking-wide`) is a utility's key and never a
 * custom property: `var(--font-weight-semibold)` in a stylesheet reads nothing and the weight falls
 * back to the inherited one. A stylesheet reads the token the key names (`--text-h3-weight`), and
 * `scripts/css-vars.ts` fails a read of the key (2026-09-30). Breakpoints and container sizes are literal because
 * a media or container query cannot read a custom property, and the loops sit in a plain block
 * so their keyframes emit.
 */
export function emitTailwind(tree: TokenTree): string {
  const [loop] = tree.loops;
  if (loop === undefined) throw new Error("the token tree names no animation loop for the spin and pulse utilities");
  const lines: string[] = [];
  const themed = { ...tree.semantic.light, ...tree.chrome };
  for (const name of Object.keys(themed)) {
    if (name.startsWith("color-")) lines.push(`  --${name}: var(--${name});`);
  }
  for (const name of Object.keys(tree.shadcn)) {
    if (name !== "radius") lines.push(`  --color-${name}: var(--${name});`);
  }
  lines.push("  --spacing: var(--space-2);");
  const radii: Readonly<Record<string, string>> = {
    xs: "calc(var(--radius-xs) / 2)",
    sm: "var(--radius-xs)",
    md: "var(--radius-sm)",
    lg: "var(--radius-md)",
    xl: "var(--radius-lg)",
    full: "var(--radius-full)",
  };
  for (const [name, value] of Object.entries(radii)) lines.push(`  --radius-${name}: ${value};`);
  for (const name of Object.keys(tree.primitives)) {
    if (name.startsWith("ease-")) lines.push(`  --${name}: var(--${name});`);
    if (name.startsWith("font-")) lines.push(`  --${name}: var(--${name});`);
    if (name.startsWith("text-") && name.endsWith("-size")) {
      const step = name.slice("text-".length, -"-size".length);
      lines.push(`  --text-${step}: var(--text-${step}-size);`);
      lines.push(`  --text-${step}--line-height: var(--text-${step}-line);`);
      lines.push(`  --text-${step}--font-weight: var(--text-${step}-weight);`);
      lines.push(`  --text-${step}--letter-spacing: var(--text-${step}-tracking);`);
    }
  }
  const tailwindText: Readonly<Record<string, string>> = { xs: "caption", sm: "body", base: "h3", lg: "h2", xl: "h2", "2xl": "h1", "3xl": "display" };
  for (const [name, step] of Object.entries(tailwindText)) {
    lines.push(`  --text-${name}: var(--text-${step}-size);`);
    lines.push(`  --text-${name}--line-height: var(--text-${step}-line);`);
  }
  lines.push("  --font-weight-normal: var(--text-body-weight);");
  lines.push("  --font-weight-medium: var(--control-font-weight);");
  lines.push("  --font-weight-semibold: var(--text-h3-weight);");
  lines.push("  --tracking-wide: var(--text-micro-tracking);");
  for (const name of Object.keys(tree.semantic.light)) {
    if (name.startsWith("elevation-")) lines.push(`  --shadow-${name.slice("elevation-".length)}: var(--${name});`);
  }
  const tailwindShadow: Readonly<Record<string, string>> = { xs: "1", sm: "1", md: "2", lg: "3", xl: "3" };
  for (const [name, step] of Object.entries(tailwindShadow)) lines.push(`  --shadow-${name}: var(--elevation-${step});`);
  lines.push("  --shadow-kbd: 0 var(--kbd-edge) 0 0 var(--color-border-subtle);");
  lines.push("  --default-transition-duration: var(--duration-base);");
  lines.push("  --default-transition-timing-function: var(--ease-in-out);");
  const breakpoints = Object.entries(tree.primitives)
    .filter(([name]) => name.startsWith("bp-"))
    .map(([name, value]) => `  --breakpoint-${name.slice("bp-".length)}: ${cssValue(value)};`);
  const containers = Object.entries(tree.components)
    .filter(([name]) => name.startsWith("container-"))
    .map(([name, value]) => `  --${name}: ${cssValue(value)};`);
  return [
    "/* Generated by packages/tokens/src/emit/build.ts. Do not edit. */",
    "@theme {",
    "  --*: initial;",
    ...breakpoints,
    ...containers,
    `  --animate-spin: spin var(--${loop}) var(--ease-linear) infinite;`,
    `  --animate-pulse: pulse var(--${loop}) var(--ease-in-out) infinite;`,
    `  --animate-progress-slide: progress-slide var(--${loop}) var(--ease-linear) infinite;`,
    "  @keyframes spin {",
    "    to {",
    "      transform: rotate(360deg);",
    "    }",
    "  }",
    "  @keyframes pulse {",
    "    50% {",
    "      opacity: 0.5;",
    "    }",
    "  }",
    "  @keyframes progress-slide {",
    "    from {",
    "      transform: translateX(-100%);",
    "    }",
    "    to {",
    "      transform: translateX(250%);",
    "    }",
    "  }",
    "}",
    "@theme inline reference {",
    ...lines,
    "}",
    "@custom-variant dark {",
    '  &:where([data-theme="dark"], [data-theme="dark"] *) {',
    "    @slot;",
    "  }",
    "  @media (prefers-color-scheme: dark) {",
    '    &:where(:root:not([data-theme="light"]), :root:not([data-theme="light"]) *) {',
    "      @slot;",
    "    }",
    "  }",
    "}",
    "",
  ].join("\n");
}
